import {
  Box,
  BudgetFilter,
  BudgetFilterProps,
  Icon,
  Heading,
  Inline,
  InlineProps,
  MultiSelect,
  Stack,
  Text,
  Tiles,
  TilesProps
} from '@front10/base-elements';
import { DateRangePicker as DatePicker } from '@front10/datepicker';
import { Item } from '@react-stately/collections';
import differenceInDays from 'date-fns/differenceInDays';
import startOfToday from 'date-fns/startOfToday';
import formatISO from 'date-fns/formatISO';
import * as React from 'react';
import { useTrackingChange } from '../../../_integrations/everymundo/tracking/ticketEvents';
import create from 'zustand';
import createContext from 'zustand/context';
import { devtools } from 'zustand/middleware';
import { Select, SelectProps as SelectFilterProps } from '../../../packages/base-elements/Select';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from './Tabs';
import { SingleDateRangePickerProps } from '@front10/datepicker';
import { EventData } from './eventsData';
import { useTranslation } from '@front10/utils';

export interface FilterContainerProps {
  panels: FilterPanelProps[];
}

interface FilterPanelProps {
  id: string;
  displayPanel: boolean;
  title: string;
  filters: FilterProps[];
  secondaryFilters?: SecondaryFilterProps[];
  layout?: Pick<TilesProps, 'sx' | 'columns' | 'gap' | 'colGap' | 'rowGap'>;
  secondaryLayout?: Pick<InlineProps, 'sx' | 'align' | 'alignY' | 'gap'>;
  disclaimer?: string | null;
  oneElement?: boolean;
  componentName?: string;
}

type DateRangeProps = {
  type: 'date-range';
  id: string;
  label: string;
  defaultValue?: $TSFixMe;
  optionsValue?: $TSFixMe;
  startDate: string;
  endDate: number;
} & Partial<SingleDateRangePickerProps>;

type FilterProps = MultiSelectProps | DateRangeProps;

type BudgetProps = {
  type: 'budget';
  id: string;
  label: string;
  defaultValue?: string;
  valueSortFilterSetOnUpdate?: string;
} & BudgetFilterProps;

// interface PrePopulation {
//   experience?: {
//     type?: string[];
//     name?: string[];
//   };
//   event: {
//     location?: string[];
//     date?: number[];
//     session?: string[];
//     time?: string[];
//   };
// }

type SecondaryFilterProps = SelectProps | BudgetProps | MultiSelectProps;

type SelectProps = {
  type?: 'select';
  id: string;
  label: string;
  defaultSelectedKey?: string;
  hideLabel?: boolean;
  defaultValue?: string;
  options: {
    label: string;
    value: string;
    experienceCategoriesRelated?: string[];
  }[];
} & Pick<SelectFilterProps<unknown>, 'overlayPosition'>;

export type MultiSelectProps = {
  type?: 'multi-select';
  id: string;
  label: string;
  defaultValue?: string[];
  optionsValue?: string[];
  showSearchOptions?: boolean;
  selectAllOptions?: {
    label: string;
    value: string;
  };
  noResultLabel?: string;
  togglebuttonLabel?: string;
  businessRules?: {
    isDependedFilter?: boolean;
    relatedToParameterNameR2?: string;
    relatedToParameterNameR1?: string;
    relatedToValue?: string;
  };
  options: {
    label: string;
    value: string;
    experienceCategoriesRelated?: string[];
  }[];
  alwaysEnabledDoneButton: boolean;
} & Pick<SelectFilterProps<unknown>, 'overlayPosition'>;

const options: FilterContainerProps = {
  panels: [
    {
      id: 'ticket+experience',
      displayPanel: true,
      title: 'Ticket + Experience',
      layout: {
        columns: [2, 2, 4],
        sx: {
          '& > div': { gridColumn: ['1 / -1', null, 'auto'] },
          '& > div:nth-of-type(3), & > div:nth-of-type(4)': { gridColumn: 'auto' }
        }
      },
      secondaryLayout: {
        align: 'end'
      },
      filters: [
        {
          id: 'date',
          type: 'date-range',
          label: 'Date & Key Events',
          localizationData: { _week: { dow: 1 } },
          startDate: '2023/01/9',
          endDate: 21,
          events: [
            {
              label: 'Qualifying starts',
              dates: ['2023-01-09'],
              styles: {
                backgroundColor: '#1E2886'
              }
            },
            {
              label: 'Australia Open starts',
              dates: ['2023-01-16'],
              styles: {
                backgroundColor: '#20c8ff'
              }
            },
            {
              label: "Women's Semi Final",
              dates: ['2023-01-26'],
              styles: {
                backgroundColor: 'limeGreen'
              }
            },
            {
              label: "Men's Semi Final",
              dates: ['2023-01-27', '2023-01-27'],
              styles: {
                backgroundColor: 'apricotOrange'
              }
            },
            {
              label: "Women's Final",
              dates: ['2023-01-28'],
              styles: {
                backgroundColor: 'limeGreen',
                borderWidth: '2px',
                borderStyle: 'dotted',
                borderColor: 'accent'
              }
            },
            {
              label: "Men's Final",
              dates: ['2023-01-29'],
              styles: {
                backgroundColor: 'apricotOrange',
                borderWidth: '2px',
                borderStyle: 'dotted',
                borderColor: 'accent'
              }
            }
          ]
        },
        {
          id: 'sessions',
          type: 'multi-select',
          label: 'Sessions',
          options: [
            {
              label: 'Day session',
              value: 'Day Session'
            },
            {
              label: 'Twilight session',
              value: 'Twilight session'
            },
            {
              label: 'Night session',
              value: 'Night session'
            }
          ],
          alwaysEnabledDoneButton: true
        },
        {
          id: 'ticketOffers',
          type: 'multi-select',
          label: 'Experience Category',
          options: [
            {
              label: 'Ticket Only',
              value: 'Ticket Only'
            },
            {
              label: 'Ticket + Dining',
              value: 'Ticket + Dining'
            },
            {
              label: 'Ticket + Experience',
              value: 'Ticket + Experience'
            }
          ],
          alwaysEnabledDoneButton: true
        },
        {
          id: 'arenas',
          type: 'multi-select',
          label: 'Arena',
          options: [
            {
              label: 'Margaret Court Arena',
              value: 'Margaret Court Arena'
            },
            {
              label: 'Rod Laver Arena',
              value: 'Rod Laver Arena'
            },
            {
              label: 'John Cain Arena',
              value: 'John Cain Arena'
            },
            {
              label: 'Ground Pass Only',
              value: 'Ground Pass'
            }
          ],
          alwaysEnabledDoneButton: true
        }
      ],
      secondaryFilters: [
        {
          id: 'budget',
          type: 'budget',
          label: 'Price',
          intervals: [49, 125, 175, 250, 500, 1500, 5000, 10000],
          overlayPosition: {
            placement: 'bottom'
          }
        },
        {
          id: 'sort',
          type: 'select',
          label: 'Sorting',
          defaultSelectedKey: 'recommended',
          options: [
            {
              label: 'Price High to Low',
              value: 'price_high_to_low'
            },
            {
              label: 'Price Low to High',
              value: 'price_low_to_high'
            },
            {
              label: 'Date (earliest to latest)',
              value: 'date_earliest_to_latest'
            },
            {
              label: 'Date (latest to earliest)',
              value: 'date_latest_to_earliest'
            },
            {
              label: 'Recommended',
              value: 'recommended'
            }
          ],
          overlayPosition: {
            placement: 'bottom end'
          }
        }
      ]
    },
    {
      id: 'experience',
      title: 'Add-on Experiences',
      displayPanel: true,
      disclaimer: 'Arena tickets are sold separately. Prices shown are per person',
      layout: {
        columns: [1, 1, 3]
      },
      secondaryLayout: {
        align: 'end'
      },
      filters: [
        {
          id: 'date',
          type: 'date-range',
          label: 'Date & Key Events',
          localizationData: { _week: { dow: 1 } },
          startDate: '2023/01/9',
          endDate: 21,
          events: [
            {
              label: 'Qualifying starts',
              dates: ['2023-01-09'],
              styles: {
                backgroundColor: '#1E2886'
              }
            },
            {
              label: 'Australia Open starts',
              dates: ['2023-01-16'],
              styles: {
                backgroundColor: '#20c8ff'
              }
            },
            {
              label: "Women's Semi Final",
              dates: ['2022-01-27'],
              styles: {
                backgroundColor: 'limeGreen'
              }
            },
            {
              label: "Men's Semi Final",
              dates: ['2022-01-28'],
              styles: {
                backgroundColor: 'apricotOrange'
              }
            },
            {
              label: "Women's Final",
              dates: ['2022-01-29'],
              styles: {
                backgroundColor: 'limeGreen',
                borderWidth: '2px',
                borderStyle: 'dotted',
                borderColor: 'accent'
              }
            },
            {
              label: "Men's Final",
              dates: ['2022-01-30'],
              styles: {
                backgroundColor: 'apricotOrange',
                borderWidth: '2px',
                borderStyle: 'dotted',
                borderColor: 'accent'
              }
            }
          ]
        },
        {
          id: 'sessions',
          type: 'multi-select',
          label: 'Sessions',
          options: [
            {
              label: 'Day session',
              value: 'Day session'
            },
            {
              label: 'Night session',
              value: 'Night session'
            },
            {
              label: 'Twilight session',
              value: 'Twilight session'
            }
          ],
          alwaysEnabledDoneButton: true
        },
        {
          id: 'arenas',
          type: 'multi-select',
          label: 'Arena',
          options: [
            {
              label: 'Rod Laver Arena',
              value: 'Rod Laver Arena'
            },
            {
              label: 'Margaret Court Arena',
              value: 'Margaret Court Arena'
            },
            {
              label: 'John Cain Arena',
              value: 'John Cain Arena'
            },
            {
              label: 'Ground Pass Only',
              value: 'Ground Pass'
            }
          ],
          alwaysEnabledDoneButton: true
        }
      ],
      secondaryFilters: [
        {
          id: 'budget',
          type: 'budget',
          label: 'Price',
          intervals: [49, 125, 175, 250, 500, 1500, 5000, 10000],
          overlayPosition: {
            placement: 'bottom'
          }
        },
        {
          id: 'sort',
          type: 'select',
          label: 'cmp-sorting-label',
          defaultSelectedKey: 'recommended',
          options: [
            {
              label: 'Price High to Low',
              value: 'price_high_to_low'
            },
            {
              label: 'Price Low to High',
              value: 'price_low_to_high'
            },
            {
              label: 'Date (earliest to latest)',
              value: 'date_earliest_to_latest'
            },
            {
              label: 'Date (latest to earliest)',
              value: 'date_latest_to_earliest'
            },
            {
              label: 'Recommended',
              value: 'recommended'
            }
          ],
          overlayPosition: {
            placement: 'bottom end'
          }
        }
      ]
    }
  ]
};

interface State {
  budget?: number | undefined;
  date?: undefined | string[];
  ticketOffers?: undefined | React.Key[];
  sessions?: undefined | React.Key[];
  arenas?: undefined | React.Key[];
  sort?: undefined | string;
}

interface FiltersState {
  state: Record<string, State>;
  context: string;
  ticketOffersOption: [] | undefined;
  setState: (value: Partial<State>) => void;
  setBudget: (value: Partial<State>, sort: string) => void;
  changeContext: (context: string) => void;
  changeTicketOffersOption: (idPanel: string) => void;
  onResetFilter: (value: string) => void;
  onSearch: (data: EventData, filters: any) => void;
  clearField: (field: string, parameter: string) => void;
  filters: FilterContainerProps;
}

const { Provider, useStore: useFiltersState } = createContext<FiltersState>();

export { useFiltersState };

interface Props {
  filters?: FilterContainerProps;
  prePopulation?: PrePopulation;
}

type PrePopulation = Record<
  string,
  {
    date?: undefined | number[];
    ticketOffers?: undefined | React.Key[];
    sessions?: undefined | React.Key[];
    arenas?: undefined | React.Key[];
    budget?: undefined | number;
  }
>;

function usePrePopulation(prePopulation: PrePopulation = {}): Record<string, any> {
  return React.useMemo(() => {
    return Object.fromEntries(
      Object.entries(prePopulation).map(([context, state]) => {
        return [
          context,
          {
            ...state,
            date: state?.date ? state?.date : undefined
          }
        ];
      })
    );
  }, [prePopulation]);
}

export function FiltersProvider({
  children,
  filters,
  prePopulation = {}
}: React.PropsWithChildren<Props>) {
  const track = useTrackingChange();
  const stateFromPrePopulation = usePrePopulation(prePopulation);

  const [filtersModified] = React.useState(() => ({
    ...filters,
    panels:
      filters?.panels.map((panel) => ({
        ...panel,
        filters: panel.filters?.map((filter: any) => {
          let options = undefined;
          if (filter.id === 'ticketOffers' && filter.options) {
            options = Array.from(filter.options.map((option: any) => option.value));
          }

          return {
            ...filter,
            defaultValue: stateFromPrePopulation[panel.id]?.[filter.id],
            optionsValue: options
          };
        }),
        secondaryFilters: panel.secondaryFilters?.map((sfilter) => {
          return {
            ...sfilter,
            defaultValue: stateFromPrePopulation?.[panel.id]?.[sfilter.id]

            // defaultSelectedKey: stateFromPrePopulation?.[panel.id]?.[sfilter.id]
          };
        })
      })) ?? []
  }));

  return (
    <Provider
      createStore={() =>
        create<FiltersState>(
          devtools((set, get) => ({
            filters: filtersModified,
            setBudget: (value: any, sort: string = '') => {
              set((oldState) => {
                track(oldState.state[oldState.context] ?? {}, {
                  type: 'FILTER_CHANGE',
                  payload: value
                });
                if (sort !== '') {
                  return {
                    state: {
                      ...oldState.state,
                      [oldState.context]: {
                        ...oldState.state[oldState.context],
                        ...value,
                        sort: sort
                      }
                    }
                  };
                }
                return {
                  state: {
                    ...oldState.state,
                    [oldState.context]: { ...oldState.state[oldState.context], ...value }
                  }
                };
              });
            },
            setState: (value) => {
              set((oldState) => {
                track(oldState.state[oldState.context] ?? {}, {
                  type: 'FILTER_CHANGE',
                  payload: value
                });
                return {
                  state: {
                    ...oldState.state,
                    [oldState.context]: { ...oldState.state[oldState.context], ...value }
                  }
                };
              });
            },
            changeContext: (context) => {
              set({ context });
            },
            context: filters?.panels.filter((item) => item.displayPanel).length
              ? filters?.panels.filter((item) => item.displayPanel)[0].id
              : filters?.panels[0].id || '',
            changeTicketOffersOption: (idPanel) => {
              const ticketOffersOption = filtersModified?.panels.filter((item) => item.displayPanel)
                .length
                ? filtersModified?.panels
                    .filter((item) => item.displayPanel && item.id === idPanel)[0]
                    .filters.filter((item) => item.id === 'ticketOffers')[0]?.optionsValue
                : filtersModified?.panels[0].filters.filter((item) => item.id === 'ticketOffers')[0]
                    ?.optionsValue || undefined;
              set({ ticketOffersOption });
            },
            ticketOffersOption: filtersModified?.panels.filter((item) => item.displayPanel).length
              ? filtersModified?.panels
                  .filter((item) => item.displayPanel)[0]
                  .filters.filter((item) => item.id === 'ticketOffers')[0]?.optionsValue
              : filtersModified?.panels[0].filters.filter((item) => item.id === 'ticketOffers')[0]
                  ?.optionsValue || undefined,
            state: stateFromPrePopulation,
            onResetFilter: (value) => {
              const currentState = get();
              track(currentState.state[currentState.context] ?? {}, {
                type: 'RESET_FILTER',
                payload: value
              });
            },
            onSearch: (data: EventData, filters: any) => {
              const currentState = get();
              track(currentState.state[currentState.context] ?? {}, {
                type: 'SEARCH',
                payload: { ...data, filters }
              });
            },
            clearField: (field: string, parameter: string) => {
              return set((state: any) => {
                let temporalState = state.state[`${parameter}`];
                if (temporalState) {
                  delete temporalState[`${field}`];
                  return {
                    ...state,
                    state: {
                      ...state.state,
                      ...temporalState
                    }
                  };
                } else {
                  return state;
                }
              });
            }
          }))
        )
      }
    >
      {children}
    </Provider>
  );
}

export function Filters({ componentName = 'premium-experience-grid' }: { componentName: string }) {
  const changeContext = useFiltersState((state) => state.changeContext);
  const changeTicketOffersOption = useFiltersState((state) => state.changeTicketOffersOption);
  const filters = useFiltersState((state) => state.filters);
  const filterContainer = filters ?? options;
  const translate = useTranslation();

  if (filterContainer.panels.length === 1) {
    return (
      <FilterPanel
        oneElement
        componentName={componentName}
        showTitle={true}
        {...filterContainer.panels[0]}
      />
    );
  }

  if (filterContainer.panels.filter((panel) => panel.displayPanel).length === 0) {
    return (
      <Tabs
        onChange={(index) => {
          changeContext(filterContainer.panels[index].id);
          changeTicketOffersOption(filterContainer.panels[index].id);
        }}
      >
        <TabList>
          {filterContainer.panels.map((filterPanel, index) => (
            <Tab key={filterPanel.id || index}>
              {translate(filterPanel.title, filterPanel.title)}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {filterContainer.panels.map((panel, index) => (
            <TabPanel key={panel.id || index}>
              <FilterPanel componentName={componentName} {...panel} />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    );
  }

  return (
    <Tabs
      onChange={(index) => {
        changeContext(filterContainer.panels[index].id);
        changeTicketOffersOption(filterContainer.panels[index].id);
      }}
    >
      <TabList>
        {filterContainer.panels
          .filter((panel) => panel.displayPanel)
          .map((filterPanel, index) => (
            <Tab key={filterPanel.id || index}>
              {translate(filterPanel.title, filterPanel.title)}
            </Tab>
          ))}
      </TabList>

      <TabPanels>
        {filterContainer.panels
          .filter((panel) => panel.displayPanel)
          .map((panel, index) => (
            <TabPanel key={panel.id || index}>
              <FilterPanel componentName={componentName} {...panel} />
            </TabPanel>
          ))}
      </TabPanels>
    </Tabs>
  );
}

// TODO: Hardcoded to avoid having to configure in existing implementations.
// It can be removed once we have access again to configurations in EM.
const datePickerLocalizationOptions = { _week: { dow: 1 } };

function FilterPanel({
  title,
  showTitle,
  filters,
  secondaryFilters,
  secondaryLayout,
  disclaimer,
  oneElement = false,
  layout,
  id,
  componentName
}: FilterPanelProps & { showTitle?: boolean }) {
  const setState = useFiltersState((state) => state.setState);
  const setBudget = useFiltersState((state) => state.setBudget);
  const onResetFilter = useFiltersState((state) => state.onResetFilter);
  const translate = useTranslation();
  const selectFilterRef = React.useRef<any>(null);
  const wasUpdateBudgetRef = React.useRef<boolean>(false);

  const content = (
    <Stack gap={2}>
      {showTitle ? (
        <Box>
          <Heading variant="heading1" as="h2">
            {translate(`cmp-${componentName}-filter-title`, title)}
          </Heading>
        </Box>
      ) : null}
      {disclaimer ? (
        <Inline role="contentinfo" variant="disclaimers.neutral" gap={1} alignY="center">
          <Icon icon="info-circle" />
          <Text variant="disclaimer" color="currentColor">
            {translate(`cmp-${componentName}-disclaimer`, disclaimer)}
          </Text>
        </Inline>
      ) : null}
      <Tiles columns={layout?.columns} sx={layout?.sx} gap={2}>
        {filters.map((filter) => {
          if (filter.type === 'date-range') {
            return (
              <DatePicker
                key={filter.id}
                availableAtoms={['Calendar', 'Footer']}
                footer={{ elementsToShow: ['DoneButton', 'CancelButton'] }}
                numberOfMonths={1}
                departure={{
                  start: differenceInDays(new Date(filter?.startDate), startOfToday()),
                  end: filter?.endDate
                }}
                navigationItem={{
                  showDecreaseMonthButton: false,
                  showIncreaseMonthButton: false
                }}
                localizationData={datePickerLocalizationOptions}
                {...filter}
                onClear={() => onResetFilter(filter.id)}
                onChange={(events) => {
                  setState({
                    [filter.id]: events.dates.map((date) =>
                      formatISO(date, { representation: 'date' })
                    )
                  });
                }}
              />
            );
          }
          if (filter.type === 'multi-select' || filter.type === undefined) {
            return (
              <MultiSelect
                key={filter.id}
                label={translate(filter.label, filter.label)}
                onClear={() => onResetFilter(filter.id)}
                defaultValue={filter.defaultValue}
                onChange={(selection) => {
                  setState({
                    [filter.id]:
                      selection !== 'all' && selection.size !== 0
                        ? Array.from(selection)
                        : undefined
                  });
                }}
                showSearchOptions={filter.showSearchOptions}
                selectAllOptions={filter.selectAllOptions}
                filter={filter}
                panelId={id}
                componentName={componentName}
                alwaysEnabledDoneButton={filter.alwaysEnabledDoneButton}
              >
                {filter.options.map((event) => (
                  <Item key={event.value}>{translate(event.label, event.label)}</Item>
                ))}
              </MultiSelect>
            );
          }
          return null;
        })}
      </Tiles>
    </Stack>
  );

  return (
    <>
      {oneElement && <Box variant="filtersContainers.secondary">{content}</Box>}
      {!oneElement && content}
      {secondaryFilters ? (
        <Stack variant="filtersContainers.secondaryContainer">
          <Inline gap={2} wrap={true} {...secondaryLayout}>
            {secondaryFilters.map((filter: SecondaryFilterProps) => {
              if (filter.type === 'budget') {
                return (
                  <BudgetFilter
                    {...filter}
                    key={filter.id}
                    label={translate(filter.label, filter.label)}
                    onClear={() => onResetFilter(filter.id)}
                    onChange={(value) => {
                      if (filter?.valueSortFilterSetOnUpdate) {
                        wasUpdateBudgetRef.current = true;
                        selectFilterRef?.current?.selectOption(filter?.valueSortFilterSetOnUpdate);
                        setBudget({ [filter.id]: value }, filter?.valueSortFilterSetOnUpdate);
                      } else {
                        setState({ [filter.id]: value });
                      }
                    }}
                  />
                );
              }
              if (filter.type === 'select') {
                const { options, id, ...rest } = filter;
                return (
                  <Select
                    {...rest}
                    key={filter.id}
                    label={translate(filter.label, filter.label)}
                    onSelectionChange={(selection) => {
                      if (wasUpdateBudgetRef?.current === false) {
                        setState({ [id]: selection.toString() });
                      }
                      wasUpdateBudgetRef.current = false;
                    }}
                    ref={selectFilterRef}
                  >
                    {options.map((element) => (
                      <Item key={element.value}>{translate(element.label, element.label)}</Item>
                    ))}
                  </Select>
                );
              }
              if (filter.type === 'multi-select') {
                const {
                  options,
                  label,
                  defaultValue,
                  showSearchOptions,
                  selectAllOptions
                } = filter;
                return (
                  <MultiSelect
                    key={filter.id}
                    label={translate(label, label)}
                    onClear={() => onResetFilter(filter.id)}
                    defaultValue={defaultValue}
                    onChange={(selection) => {
                      setState({
                        [filter.id]:
                          selection !== 'all' && selection.size !== 0
                            ? Array.from(selection)
                            : undefined
                      });
                    }}
                    showSearchOptions={showSearchOptions}
                    selectAllOptions={selectAllOptions}
                    filter={filter}
                    panelId={id}
                    secondaryFilter={true}
                    componentName={componentName}
                    alwaysEnabledDoneButton={filter.alwaysEnabledDoneButton}
                  >
                    {options.map((event: any) => (
                      <Item key={event.value}>{translate(event.label, event.label)}</Item>
                    ))}
                  </MultiSelect>
                );
              }
              return null;
            })}
          </Inline>
        </Stack>
      ) : null}
    </>
  );
}
